import React, { useState, useEffect } from 'react';
import './WalletConnection.css';
import MetaMaskService from '../services/metamask';
import ApiService from '../services/api';

const WalletConnection = ({ connected, onConnect, address, onAddressChange }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');

  // Vérifier si MetaMask est installé
  const isMetaMaskInstalled = () => {
    return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
  };

  // Connecter MetaMask et authentifier
  const connectWallet = async () => {
    if (!MetaMaskService.isMetaMaskInstalled()) {
      setError('MetaMask n\'est pas installé. Veuillez l\'installer pour continuer.');
      return;
    }

    setIsConnecting(true);
    setError('');

    try {
      // Authentification complète avec MetaMask
      const authResult = await MetaMaskService.authenticate();
      
      if (authResult.success) {
        // Authentifier avec notre backend
        const apiResult = await ApiService.authenticateWithMetaMask(
          authResult.address,
          authResult.signature,
          authResult.message
        );

        if (apiResult.success) {
          onAddressChange(authResult.address);
          onConnect(true);
          
          // Écouter les changements
          MetaMaskService.onAccountsChanged(handleAccountsChanged);
          MetaMaskService.onChainChanged(handleChainChanged);

          // Déclencher l'événement de connexion réussie avec les infos utilisateur
          if (window.onUserAuthenticated) {
            window.onUserAuthenticated(apiResult.user);
          }
        }
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      setError(error.message || 'Erreur lors de la connexion à MetaMask');
    } finally {
      setIsConnecting(false);
    }
  };

  // Déconnecter le wallet
  const disconnectWallet = () => {
    onConnect(false);
    onAddressChange('');
    
    // Supprimer les listeners
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    }
  };

  // Gérer le changement de compte
  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      onAddressChange(accounts[0]);
    }
  };

  // Gérer le changement de réseau
  const handleChainChanged = () => {
    window.location.reload();
  };

  // Vérifier la connexion au chargement
  useEffect(() => {
    const checkConnection = async () => {
      if (isMetaMaskInstalled()) {
        try {
          const accounts = await window.ethereum.request({
            method: 'eth_accounts',
          });
          
          if (accounts.length > 0) {
            onAddressChange(accounts[0]);
            onConnect(true);
          }
        } catch (error) {
          console.error('Erreur lors de la vérification de la connexion:', error);
        }
      }
    };

    checkConnection();
  }, []);

  // Formater l'adresse pour l'affichage
  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <div className="wallet-connection">
      <div className="wallet-header">
        <h3>🦊 Connexion Wallet</h3>
        <div className={`status-indicator ${connected ? 'connected' : 'disconnected'}`}>
          {connected ? 'Connecté' : 'Déconnecté'}
        </div>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {ApiService.isDemoMode() && connected && (
        <div className="demo-info-message">
          🎭 Mode démo actif - Backend non disponible, données simulées
        </div>
      )}

      {!connected ? (
        <div className="connection-section">
          <p>Connectez votre wallet MetaMask pour accéder au trading bot</p>
          
          {!isMetaMaskInstalled() ? (
            <div className="install-metamask">
              <p>MetaMask n'est pas détecté</p>
              <a 
                href="https://metamask.io/download.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="install-button"
              >
                📥 Installer MetaMask
              </a>
            </div>
          ) : (
            <button 
              className="connect-button"
              onClick={connectWallet}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <div className="spinner"></div>
                  Connexion...
                </>
              ) : (
                <>
                  🔗 Connecter MetaMask
                </>
              )}
            </button>
          )}
        </div>
      ) : (
        <div className="connected-section">
          <div className="wallet-info">
            <div className="address-display">
              <span className="label">Adresse:</span>
              <span className="address">{formatAddress(address)}</span>
              <button 
                className="copy-button"
                onClick={() => navigator.clipboard.writeText(address)}
                title="Copier l'adresse"
              >
                📋
              </button>
            </div>
            
            <div className="network-info">
              <span className="label">Réseau:</span>
              <span className="network">Ethereum Mainnet</span>
            </div>
          </div>

          <button 
            className="disconnect-button"
            onClick={disconnectWallet}
          >
            🔌 Déconnecter
          </button>
        </div>
      )}
    </div>
  );
};

export default WalletConnection;
